/**
 * Aprendizaje Superficial y Profundo — replica AprendizajeSuperficialProfundo.aspx.cs.
 *
 * Mecánica:
 * - Los items de secuencia tienen marcadores de tipo de codificación en el Valor:
 *   (S) = semántico, (F) = fonológico, (P) = perceptual
 * - Fases:
 *   1. RUNNING: presentar items uno a uno (tiempo_mili ms) o consignas con botón
 *      - Para items con es_consigna: mostrar texto + botón continuar
 *      - Para items con es_pregunta_boolena: mostrar texto + Sí/No
 *      - Para items con respuesta de texto: mostrar + input
 *      - Para items de solo presentación: auto-avanzar
 *   2. DONE: mostrar accuracy por tipo de codificación (S/F/P)
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTestConfig, TYPE_TO_API_PATH } from '../../api/client';
import TestLayout, {
  DoneScreen, YesNoButtons, ContinueButton, LoadingScreen, ErrorScreen, useSpeedMultiplier,
  useFocusOnStep,
} from './shared/TestLayout';
import SafeHtml from './shared/SafeHtml';
import { registrarUso } from '../../api/usoClient';
import SaveResults from './shared/SaveResults';
import { navigateAfterTest } from '../../api/ensayosClient';

const PHASE = { LOADING: 'loading', RUNNING: 'running', DONE: 'done' };

// Extrae el marcador de tipo del Valor.
// Soporta "(S) BOTELLA" (inicio) y "BOTELLA(s)" (final).
function parseValor(valor) {
  const s = (valor ?? '').trim();
  let m = s.match(/^\((S|F|P)\)\s*(.*)/i);
  if (m) return { tipo: m[1].toUpperCase(), texto: m[2].trim() };
  m = s.match(/^(.*?)\s*\((S|F|P)\)$/i);
  if (m) return { tipo: m[2].toUpperCase(), texto: m[1].trim() };
  return { tipo: null, texto: s };
}

export default function TestRunAprendizaje() {
  const { lenguajeId, categoriaId, tareaId, type, testId } = useParams();
  const navigate = useNavigate();
  const apiType = TYPE_TO_API_PATH[type] ?? type;

  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState(PHASE.LOADING);
  const [seqIndex, setSeqIndex] = useState(0);
  const [currentInput, setCurrentInput] = useState('');
  const [responses, setResponses] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const timerRef       = useRef(null);
  const usageLoggedRef = useRef(false);
  const responsesRef   = useRef([]);
  responsesRef.current = responses;

  useEffect(() => {
    getTestConfig(apiType, testId)
      .then((data) => { setConfig(data); setPhase(PHASE.RUNNING); })
      .catch((e) => setError(e.message));
  }, [apiType, testId]);

  const goBack = useCallback(() => {
    navigateAfterTest(
      navigate,
      `/lenguaje/${lenguajeId}/categoria/${categoriaId}/tarea/${tareaId}`,
    );
  }, [navigate, lenguajeId, categoriaId, tareaId]);

  useEffect(() => {
    if (phase !== PHASE.DONE || usageLoggedRef.current) return;
    usageLoggedRef.current = true;
    const r = responsesRef.current;
    registrarUso({
      tipo_test:   `ASP${testId}`,
      test_id:     testId,
      tarea_id:    tareaId,
      nombre_test: config?.descripcion ?? '',
      completada:  true,
      total_items: r.length,
      correctas:   r.filter((x) => x.correcto).length,
      errores:     r.filter((x) => !x.correcto).length,
    });
  }, [phase, testId, tareaId, config]);

  const handleExit = useCallback(() => {
    if (!usageLoggedRef.current) {
      usageLoggedRef.current = true;
      const r = responsesRef.current;
      registrarUso({
        tipo_test:   `ASP${testId}`,
        test_id:     testId,
        tarea_id:    tareaId,
        nombre_test: config?.descripcion ?? '',
        completada:  false,
        total_items: r.length,
        correctas:   r.filter((x) => x.correcto).length,
        errores:     r.filter((x) => !x.correcto).length,
      });
    }
    goBack();
  }, [goBack, testId, tareaId, config]);

  const speedMultiplier = useSpeedMultiplier();
  const secuencia = config?.ListaValores?.Secuencia ?? [];
  const tiempoMili = (config?.tiempo_mili ?? 1500) / speedMultiplier;
  const conclusiones = config?.Concluciones ?? '';

  const currentItem = secuencia[seqIndex];
  const { tipo, texto } = parseValor(currentItem?.Valor);
  const isConsigna = !!currentItem?.es_consigna;
  const isBoolQuestion = !!currentItem?.es_pregunta_boolena;
  const hasExpectedAnswer = currentItem?.respuesta != null && currentItem?.respuesta !== '';
  const isPresentationOnly = !isConsigna && !isBoolQuestion && !hasExpectedAnswer;
  const showAnswerInput = phase === PHASE.RUNNING && !!currentItem && !isConsigna && !isBoolQuestion && hasExpectedAnswer;
  const answerInputRef = useFocusOnStep(showAnswerInput, seqIndex);

  const advanceSeq = useCallback(() => {
    setSeqIndex((i) => {
      const next = i + 1;
      if (next >= secuencia.length) { setPhase(PHASE.DONE); return i; }
      return next;
    });
    setCurrentInput('');
  }, [secuencia.length]);

  const recordAndAdvance = useCallback((respuesta, correcto) => {
    setResponses((r) => [...r, { tipo, texto, respuesta, correcto }]);
    advanceSeq();
  }, [tipo, texto, advanceSeq]);

  // Auto-avanzar items de solo presentación
  useEffect(() => {
    if (phase !== PHASE.RUNNING || !currentItem || !isPresentationOnly) return;
    timerRef.current = setTimeout(() => advanceSeq(), tiempoMili);
    return () => clearTimeout(timerRef.current);
  }, [phase, currentItem, isPresentationOnly, seqIndex, tiempoMili, advanceSeq]);

  const handleRetry = () => {
    setSeqIndex(0); setCurrentInput(''); setResponses([]); setShowResults(false); setPhase(PHASE.RUNNING);
  };

  if (error) return <ErrorScreen message={error} onBack={goBack} />;
  if (phase === PHASE.LOADING || !config) return <LoadingScreen />;

  if (phase === PHASE.DONE) {
    // Agrupar por tipo de codificación
    const byTipo = {};
    responses.forEach(({ tipo: t, correcto }) => {
      if (!t) return;
      if (!byTipo[t]) byTipo[t] = { correctos: 0, total: 0 };
      byTipo[t].total++;
      if (correcto) byTipo[t].correctos++;
    });

    const labels = { S: 'Semántico', F: 'Fonológico', P: 'Perceptual' };

    const registros = responses.map((r) => ({
      pregunta:        r.tipo ? `(${r.tipo}) ${r.texto}` : r.texto,
      respuesta:       '',
      respuestaUsuario:String(r.respuesta ?? ''),
      correcta:        !!r.correcto,
      tiempoRespuesta: '',
    }));

    return (
      <DoneScreen conclusiones={showResults ? null : conclusiones} onRetry={handleRetry} onBack={goBack}>
        <SaveResults tipo={`ASP${testId}`} registros={registros} />
        {showResults ? (
          <div className="mt-4 w-full max-w-xs mx-auto">
            {Object.keys(byTipo).length > 0 && (
              <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden mb-4">
                <thead className="bg-violet-800 text-white font-semibold">
                  <tr>
                    <th className="px-4 py-2 text-left">Codificación</th>
                    <th className="px-4 py-2 text-right">Aciertos</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(byTipo).sort(([a], [b]) => ['S','F','P'].indexOf(a) - ['S','F','P'].indexOf(b)).map(([t, d]) => (
                    <tr key={t} className="border-t border-slate-200 text-slate-700">
                      <td className="px-4 py-3">{(labels[t] ?? t).toUpperCase()}</td>
                      <td className="px-4 py-3 text-right">{d.correctos} DE {d.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="flex justify-center">
              <button
                onClick={() => setShowResults(false)}
                className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm bg-white hover:bg-slate-50 cursor-pointer"
              >
                OCULTAR RESULTADOS
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowResults(true)}
            className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm bg-white hover:bg-slate-50 mt-2 cursor-pointer"
          >
            QUIERO SABER MIS RESULTADOS
          </button>
        )}
      </DoneScreen>
    );
  }

  if (!currentItem) { setPhase(PHASE.DONE); return null; }

  return (
    <TestLayout current={seqIndex + 1} total={secuencia.length} onExit={handleExit}>

      {/* CONSIGNA */}
      {isConsigna && (
        <>
          <SafeHtml
            as="p"
            html={texto || currentItem.Valor}
            className="text-center text-slate-800 whitespace-pre-wrap leading-tight max-w-5xl mb-4"
            style={{ fontSize: '33px', fontWeight: 700 }}
          />
          <ContinueButton onClick={advanceSeq} />
        </>
      )}

      {/* PRESENTACIÓN SOLA (auto-avanza) */}
      {isPresentationOnly && (
        <div className="text-center">
          <SafeHtml as="div" html={texto} className="text-6xl font-medium text-slate-800" />
        </div>
      )}

      {/* PREGUNTA BOOLEANA */}
      {!isConsigna && isBoolQuestion && (
        <>
          <SafeHtml as="div" html={texto} className="text-5xl font-medium text-slate-800 text-center mb-8" />
          <YesNoButtons
            onYes={() => recordAndAdvance('SI', currentItem.es_verdadero ? true : false)}
            onNo={() => recordAndAdvance('NO', !currentItem.es_verdadero ? true : false)}
          />
        </>
      )}

      {/* RESPUESTA DE TEXTO */}
      {!isConsigna && !isBoolQuestion && hasExpectedAnswer && (
        <>
          <SafeHtml as="div" html={texto} className="text-3xl font-bold text-slate-800 text-center mb-4" />
          <input
            ref={answerInputRef}
            type="text"
            value={currentInput}
            onChange={(e) => setCurrentInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const correct = currentInput.trim().toUpperCase() === String(currentItem.respuesta).trim().toUpperCase();
                recordAndAdvance(currentInput, correct);
              }
            }}
            className="max-w-md w-full rounded-lg bg-white border border-slate-300 text-slate-800 p-4 text-center text-lg focus:border-emerald-500 outline-none"
            placeholder="Ingrese su respuesta"
          />
          <button
            onClick={() => {
              const correct = currentInput.trim().toUpperCase() === String(currentItem.respuesta).trim().toUpperCase();
              recordAndAdvance(currentInput, correct);
            }}
            className="border-0 bg-transparent cursor-pointer p-0 hover:opacity-80 transition-opacity mt-4"
          >
            <img src="/assets/img/Ok-icon.png" alt="Siguiente" className="h-20 w-auto" />
          </button>
        </>
      )}

    </TestLayout>
  );
}
